import {useCallback, useMemo, useState} from 'react';

import {getRenderedGridRows} from 'studies/typed/shared/procedure-runs/conditions/conditionGroupGridRows';
import type {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import type {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {
  getProcedureRunDesignFlowchartEdgeId,
  parseProcedureRunDesignFlowchartEdgeId,
} from './ProcedureRunDesignFlowchartEdge';
import type {FlowchartEdgeSpec} from './ProcedureRunDesignFlowchartLayoutUtils';
import {getProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';

export interface UseProcedureRunDesignEdgeEditsResult {
  /** Edges drawn this session, pruned to endpoints that still exist. */
  drawnEdgeSpecs: Array<FlowchartEdgeSpec>;
  /** Ids of persisted edges removed this session, pruned to endpoints that still exist. */
  removedEdgeIds: ReadonlySet<string>;
  /** Records a drawn edge. */
  addDrawnEdge: (spec: FlowchartEdgeSpec) => void;
  /** Records drawn edges. */
  addDrawnEdges: (specs: ReadonlyArray<FlowchartEdgeSpec>) => void;
  /** Removes an edge by id: drops any matching draft, and adds the id to removedEdgeIds
   * when the edge is persisted, so it is left out of the next save payload. */
  removeEdge: (edgeId: string) => void;
  /** Clears all edge edits. */
  resetEdits: () => void;
  /** Combines drawn edges into the derived list, then drops removed ones. */
  applyEdits: () => Array<FlowchartEdgeSpec>;
}

function edgeSpecId(spec: FlowchartEdgeSpec): string {
  return getProcedureRunDesignFlowchartEdgeId(spec.sourceNodeId, spec.targetNodeId);
}

/** Collects IDs for live grid nodes, retaining backend replicate numbering across archived gaps. */
function getLiveNodeIds(
  conditions: ReadonlyArray<ConditionGroup>,
  methods: ReadonlyArray<ExperimentMethod>
): ReadonlySet<string> {
  const liveNodeIds = new Set<string>();
  getRenderedGridRows(conditions, methods, false).forEach(({conditionGroupIndex, replicateIndex, cells}) => {
    cells.forEach(({methodId}) => {
      liveNodeIds.add(
        getProcedureRunDesignFlowchartNodeId({
          conditionIndex: conditionGroupIndex,
          replicateIndex,
          methodId,
        })
      );
    });
  });
  return liveNodeIds;
}

/**
 * Keeps only edges whose endpoints still exist, so removing a condition can't leave a dangling edge.
 */
export function pruneDrawnEdgeSpecs(
  drawnEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>,
  liveNodeIds: ReadonlySet<string>
): Array<FlowchartEdgeSpec> {
  return drawnEdgeSpecs.filter(
    (spec) => liveNodeIds.has(spec.sourceNodeId) && liveNodeIds.has(spec.targetNodeId)
  );
}

/**
 * Keeps only removed ids whose endpoints still exist, mirroring draft pruning so removing a condition
 * can't strand a removed id.
 */
export function pruneRemovedEdgeIds(
  removedEdgeIds: ReadonlySet<string>,
  liveNodeIds: ReadonlySet<string>
): ReadonlySet<string> {
  const pruned = new Set<string>();
  for (const edgeId of removedEdgeIds) {
    const {sourceNodeId, targetNodeId} = parseProcedureRunDesignFlowchartEdgeId(edgeId);
    if (liveNodeIds.has(sourceNodeId) && liveNodeIds.has(targetNodeId)) {
      pruned.add(edgeId);
    }
  }
  return pruned;
}

/** Appends drawn specs to derived specs, dropping any drawn spec already present in the derived set. */
export function combineEdgeSpecs(
  derivedEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>,
  drawnEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>
): Array<FlowchartEdgeSpec> {
  const seenIds = new Set(derivedEdgeSpecs.map((spec) => edgeSpecId(spec)));
  const combined = [...derivedEdgeSpecs];
  for (const spec of drawnEdgeSpecs) {
    const id = edgeSpecId(spec);
    if (!seenIds.has(id)) {
      seenIds.add(id);
      combined.push(spec);
    }
  }
  return combined;
}

/** Drops specs whose edge id is in the removed set, including derived edges. */
export function applyEdgeRemovals(
  specs: ReadonlyArray<FlowchartEdgeSpec>,
  removedEdgeIds: ReadonlySet<string>
): Array<FlowchartEdgeSpec> {
  if (removedEdgeIds.size === 0) {
    return [...specs];
  }
  return specs.filter((spec) => !removedEdgeIds.has(edgeSpecId(spec)));
}

/**
 * Owns the run tab's in-session edge edits: drafts added and existing edges removed. Edits are pruned as
 * conditions change, merged with `derivedEdgeSpecs` into the save payload via `applyEdits`, and cleared
 * only on reset. Removing an edge that exists in `derivedEdgeSpecs` records its id so it drops from the
 * next save; removing a purely drawn edge just drops the draft, so a drawn-then-removed edge leaves no
 * trace.
 */
export function useProcedureRunDesignEdgeEdits(
  conditions: ReadonlyArray<ConditionGroup>,
  methods: ReadonlyArray<ExperimentMethod>,
  derivedEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec> = []
): UseProcedureRunDesignEdgeEditsResult {
  const [drawnEdgeSpecs, setDrawnEdgeSpecs] = useState<Array<FlowchartEdgeSpec>>([]);
  const [removedEdgeIds, setRemovedEdgeIds] = useState<ReadonlySet<string>>(() => new Set());

  // Reconcile against the current conditions during render (React's adjust-state-on-prop-change
  // pattern). Dropping edits whose endpoints no longer resolve keeps a stale edit from reviving onto a
  // shifted node id. Reuse the existing reference when nothing changed so downstream memos don't churn.
  const liveNodeIds = useMemo(() => getLiveNodeIds(conditions, methods), [conditions, methods]);
  const reconciledEdgeSpecs = pruneDrawnEdgeSpecs(drawnEdgeSpecs, liveNodeIds);
  const nextEdgeSpecs =
    reconciledEdgeSpecs.length === drawnEdgeSpecs.length ? drawnEdgeSpecs : reconciledEdgeSpecs;
  if (nextEdgeSpecs !== drawnEdgeSpecs) {
    setDrawnEdgeSpecs(nextEdgeSpecs);
  }

  const reconciledRemovedEdgeIds = pruneRemovedEdgeIds(removedEdgeIds, liveNodeIds);
  const nextRemovedEdgeIds =
    reconciledRemovedEdgeIds.size === removedEdgeIds.size ? removedEdgeIds : reconciledRemovedEdgeIds;
  if (nextRemovedEdgeIds !== removedEdgeIds) {
    setRemovedEdgeIds(nextRemovedEdgeIds);
  }

  const addDrawnEdges = useCallback((specs: ReadonlyArray<FlowchartEdgeSpec>) => {
    const ids = new Set(specs.map((spec) => edgeSpecId(spec)));
    setDrawnEdgeSpecs((current) => {
      const existingIds = new Set(current.map((spec) => edgeSpecId(spec)));
      // Filter out duplicates
      const newSpecs = specs.filter((spec) => {
        const id = edgeSpecId(spec);
        if (existingIds.has(id)) {
          return false;
        }
        existingIds.add(id);
        return true;
      });
      return newSpecs.length === 0 ? current : [...current, ...newSpecs];
    });
    setRemovedEdgeIds((current) => {
      const hasRemovedEdge = [...ids].some((id) => current.has(id));
      if (!hasRemovedEdge) {
        return current;
      }
      const next = new Set(current);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const addDrawnEdge = useCallback(
    (spec: FlowchartEdgeSpec) => {
      addDrawnEdges([spec]);
    },
    [addDrawnEdges]
  );

  const derivedEdgeIds = useMemo(
    () => new Set(derivedEdgeSpecs.map((spec) => edgeSpecId(spec))),
    [derivedEdgeSpecs]
  );

  const removeEdge = useCallback(
    (edgeId: string) => {
      setDrawnEdgeSpecs((current) => {
        const next = current.filter((spec) => edgeSpecId(spec) !== edgeId);
        return next.length === current.length ? current : next; // Preserve existing reference if no change
      });
      // Record a removal only for a persisted (derived) edge, so it drops from the next save. Removing a
      // draft edge just drops it and records nothing.
      if (derivedEdgeIds.has(edgeId)) {
        setRemovedEdgeIds((current) => (current.has(edgeId) ? current : new Set(current).add(edgeId)));
      }
    },
    [derivedEdgeIds]
  );

  const resetEdits = useCallback(() => {
    setDrawnEdgeSpecs([]);
    setRemovedEdgeIds(new Set());
  }, []);

  const applyEdits = useCallback(
    (): Array<FlowchartEdgeSpec> =>
      applyEdgeRemovals(combineEdgeSpecs(derivedEdgeSpecs, nextEdgeSpecs), nextRemovedEdgeIds),
    [derivedEdgeSpecs, nextEdgeSpecs, nextRemovedEdgeIds]
  );

  return {
    drawnEdgeSpecs: nextEdgeSpecs,
    removedEdgeIds: nextRemovedEdgeIds,
    addDrawnEdge,
    addDrawnEdges,
    removeEdge,
    resetEdits,
    applyEdits,
  };
}
